import React from "react";
import { Sparkles, Lock } from "lucide-react";

/**
 * MemberOnlyBadge — small gold-ringed pill that telegraphs "this link
 * requires a Studio membership" before the user clicks. Designed to be
 * dropped inline next to any anchor / button that links to a gated tool
 * (`/chords`, `/lyrics`, `/planner`, future members-only routes).
 *
 * Reduces the "click → redirect to login → frustration" loop on the
 * marketing page by setting expectations upfront.
 *
 * Examples:
 *
 *   // Default — small gold pill with sparkle icon + "Studio members" label
 *   <a href="/chords">
 *     Chord Atlas <MemberOnlyBadge />
 *   </a>
 *
 *   // Locked variant — communicates inaccessibility more strongly
 *   <Link to="/lyrics">
 *     Lyric Studio <MemberOnlyBadge variant="locked" />
 *   </Link>
 *
 *   // Larger size + custom label for hero/nav placements
 *   <MemberOnlyBadge size="md" label="Members only" />
 *
 * Props:
 *   size       'xs' | 'sm' | 'md'                     default 'sm'
 *   variant    'default' (sparkle, gold) | 'locked' (lock icon, muted)
 *   label      string                                  default 'Studio members'
 *   className  additional classes for parent control
 *   testId     optional data-testid override
 */
export default function MemberOnlyBadge({
  size = "sm",
  variant = "default",
  label = "Studio members",
  className = "",
  testId,
}) {
  const Icon = variant === "locked" ? Lock : Sparkles;
  const sizeClasses = {
    xs: "text-[9px] px-1.5 py-0.5 gap-1",
    sm: "text-[10px] px-2 py-0.5 gap-1",
    md: "text-[11px] px-2.5 py-1 gap-1.5",
  }[size];
  const iconSize = {
    xs: "w-2.5 h-2.5",
    sm: "w-3 h-3",
    md: "w-3.5 h-3.5",
  }[size];
  const variantClasses =
    variant === "locked"
      ? "bg-white/5 text-white/55 border-white/15 ring-white/5"
      : "bg-paul-yellow/10 text-paul-yellow border-paul-yellow/35 ring-paul-yellow/20";

  return (
    <span
      data-testid={testId || "member-only-badge"}
      title={`${label} — requires an active Studio membership`}
      className={[
        "inline-flex items-center align-middle whitespace-nowrap",
        "font-bold uppercase tracking-[0.1em]",
        "border rounded-full ring-1 ring-inset",
        sizeClasses,
        variantClasses,
        className,
      ].join(" ")}
    >
      <Icon className={iconSize} strokeWidth={2.4} />
      {label}
    </span>
  );
}
